/**
 * `devices` (§7.11, §7.3/D30) — Settings → Devices, reached from the sidebar row this PRD also
 * adds. **P15 surface 1 of five**, and the one whose §13 criterion is *"return no binding material
 * in any response"*.
 *
 * **Three things this page must not do** (PRD §3), each of which is a line of code that is
 * deliberately absent rather than a comment:
 *
 *  1. **It never re-implements revoke.** Every row's action is `DELETE /users/me/sessions*`
 *     through `./api.ts`, and the current device's is `SessionManager.signOut()` — which is the
 *     *same* revoke plus the local teardown. There is no second path, and no local "forget this
 *     device" that a network failure could leave diverging from the server.
 *  2. **It never renders binding material.** `DeviceSession` carries six fields, none of them a
 *     binding, a token, or a fragment of either — and nothing on this screen reads `session`
 *     state beyond `request`/`online`, because `SessionApi` deliberately exposes no accessor that
 *     hands a credential out.
 *  3. **The eviction path has no notification and this page is not one.** D47 accepts that an
 *     evicted device *"simply lands back on the OTP screen on its next request"*; the note at the
 *     bottom says so as a standing fact about the product, and nothing here reports a *particular*
 *     device having been evicted. Inventing that surface would be inventing an outbound channel
 *     Stage 1 does not have.
 *
 * **The cap is P2's and is not displayed.** The prototype printed `n / cap` from its own fixture;
 * the real projection carries no cap and `MAX_SESSIONS` lives in `packages/functions/auth/config.ts`,
 * which the browser cannot import. A hardcoded `3` here would be a second declaration of a
 * server constant that drifts silently — the exact failure `sessions.ts`'s own rule 1 ("cap `n`,
 * never 1 — read, never inlined") exists to prevent — so the row count is shown and the cap is
 * described in words. Logged in `implementation-notes.md` → P15.
 *
 * **Offline is empty, not stale.** `device-cache.md` caches gates, not sessions, so there is
 * nothing to render from disk; a list of devices that may already have been revoked is worse than
 * a line saying we could not ask.
 */
import { useEffect, useState } from 'react'
import { asIsoInstant } from '@selogate/core'
import { useSession } from '../../auth/context'
import { NetworkError } from '../../auth/transport'
import { AppBar, Bidi, Chip, ConfirmPopover, Icon, SecondaryButton } from '../../components'
import { useSignOut } from '../../components/auth'
import { useTranslation } from '../../i18n/I18nProvider'
import { formatRelativeTime } from '../../i18n/format'
import type { Locale } from '../../i18n/locales'
import { useDelayedIndicator } from '../../hooks/useDelayedIndicator'
import { useDismissOnBack, useNavigation } from '../../router'
import { listSessions, revokeDeviceSession, revokeOtherDeviceSessions, type DeviceSession } from './api'

/**
 * The same three-phase shape `ActivityScreen` and `PeopleScreen` use, minus their `forbidden`
 * phase: this read is scoped to the caller's own partition, so there is no refusal a viewer could
 * receive here that is not simply "no session" — and that ends the session rather than reaching
 * this screen.
 */
type ScreenState =
  | { readonly phase: 'loading' }
  | { readonly phase: 'ready'; readonly sessions: readonly DeviceSession[] }
  | { readonly phase: 'failed' }

/** Which dialog is open, if any. `current` is the sign-out confirmation §7.11 requires. */
type Pending =
  | { readonly kind: 'one'; readonly session: DeviceSession }
  | { readonly kind: 'current' }
  | { readonly kind: 'others' }

function relative(value: string, locale: Locale): string {
  try {
    return formatRelativeTime(asIsoInstant(value), locale)
  } catch {
    // A malformed timestamp degrades to the raw value, the same rule `./api.ts`'s readers follow.
    return value
  }
}

export function DevicesScreen(): JSX.Element {
  const session = useSession()
  const navigate = useNavigation()
  const signOut = useSignOut()
  const { t, locale } = useTranslation('devices')
  const { t: tCommon } = useTranslation('common')

  const [state, setState] = useState<ScreenState>({ phase: 'loading' })
  const [pending, setPending] = useState<Pending | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  // Not dismissible while a revoke is in flight, for `ConfirmPopover`'s own reason: a cancelled-
  // looking dialog over a request that is still running is how a device appears not to have been
  // signed out when it has.
  useDismissOnBack('devices:confirm', pending !== undefined, () => setPending(undefined), {
    dismissible: !busy,
  })

  async function load(): Promise<void> {
    if (!session.online) {
      setState({ phase: 'failed' })
      return
    }
    try {
      const result = await listSessions(session.request)
      setState(result.ok ? { phase: 'ready', sessions: result.data } : { phase: 'failed' })
    } catch (caught) {
      if (!(caught instanceof NetworkError)) throw caught
      setState({ phase: 'failed' })
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * One revoke, one re-read.
   *
   * **The list is re-read rather than spliced locally.** The server is the authority on which
   * sessions exist — the middleware reconciles the cap on every request (§9.2), so a row can
   * disappear for reasons this screen did not cause — and a locally spliced list would show a
   * device as gone that a concurrent sign-in had already replaced.
   */
  async function run(
    action: () => Promise<{ readonly ok: boolean; readonly status?: number }>,
  ): Promise<void> {
    setBusy(true)
    setError(undefined)
    try {
      const result = await action()
      if (!result.ok) {
        // A `404` means the row is already gone — the outcome the user asked for, reached by
        // someone else. Reporting it as a failure would invite a retry that cannot do anything.
        setError(result.status === 404 ? t('revokeGone') : t('revokeFailed'))
        await load()
        return
      }
      setPending(undefined)
      await load()
    } catch (caught) {
      if (!(caught instanceof NetworkError)) throw caught
      setError(t('revokeFailed'))
    } finally {
      setBusy(false)
    }
  }

  function confirmLabelFor(kind: Pending['kind']): string {
    if (kind === 'others') return t('revokeOthersConfirm')
    if (kind === 'current') return t('revokeCurrentConfirm')
    return t('revoke')
  }

  async function onConfirm(): Promise<void> {
    if (pending === undefined || busy) return
    if (pending.kind === 'current') {
      // **Sign-out, not a second revoke.** `signOut()` sends the same `DELETE` and then tears the
      // local session down; calling the API wrapper here would revoke the server record and leave
      // this tab holding a binding it no longer has a session for.
      setBusy(true)
      signOut()
      return
    }
    if (pending.kind === 'others') {
      await run(async () => await revokeOtherDeviceSessions(session.request))
      return
    }
    const target = pending.session.sessionId
    await run(async () => await revokeDeviceSession(session.request, target))
  }

  /** M7 — the word, not the skeletons; a failure still renders its error immediately. */
  const slowLoad = useDelayedIndicator(state.phase === 'loading')
  const sessions = state.phase === 'ready' ? state.sessions : []
  const others = sessions.filter((row) => !row.isCurrent)

  return (
    <>
      <AppBar title={t('title')} onBack={() => navigate.back()} />

      {state.phase === 'loading' ? (
        /*
         * **R5 step 7 (M7) — a delayed word over row-shaped placeholders.**
         *
         * §5.4 refuses cache-first for this list, and the refusal is the reason the wait is shaped
         * rather than removed: a stale session list still showing a device the user has just
         * revoked is not a slow screen, it is a false statement about their account security. So
         * the list only ever comes from a live read, and what M7 buys is that a fast read no longer
         * flashes a spinner on the way past.
         *
         * The skeletons render **immediately** and the word waits: the placeholders are the shape
         * of the answer arriving, which is worth showing at once, while "loading…" is a claim about
         * duration. Three rows is the median account, not a guess about this one — the real count
         * arrives with the list.
         */
        <>
          <div aria-hidden="true">
            {[0, 1, 2].map((row) => (
              <div className="row-skeleton" key={row}>
                <div>
                  <span className="sk-title" />
                  <span className="sk-sub" />
                </div>
                <span className="sk-action" />
              </div>
            ))}
          </div>
          {slowLoad ? <div className="roster-notice">{t('loading')}</div> : null}
        </>
      ) : state.phase === 'failed' ? (
        <div className="roster-notice">
          <p className="hint error">{session.online ? t('loadFailed') : t('offline')}</p>
          <SecondaryButton
            onClick={() => {
              setState({ phase: 'loading' })
              void load()
            }}
          >
            {tCommon('retry')}
          </SecondaryButton>
        </div>
      ) : (
        <>
          <div className="section-title">
            {t('sectionTitle')} — {t('count', { count: sessions.length })}
          </div>

          {sessions.map((row) => (
            <DeviceRow
              key={row.sessionId}
              row={row}
              locale={locale}
              label={row.deviceLabel ?? t('unknownDevice')}
              lastSeen={t('lastSeen', { when: relative(row.lastSeenAt, locale) })}
              firstSeen={t('firstSeen', { when: relative(row.firstSeenAt, locale) })}
              currentLabel={t('thisDevice')}
              revokeLabel={t('revoke')}
              disabled={busy}
              onRevoke={() => setPending(row.isCurrent ? { kind: 'current' } : { kind: 'one', session: row })}
            />
          ))}

          {error !== undefined ? <p className="hint error">{error}</p> : null}

          {others.length > 0 ? (
            <div className="form-row">
              <SecondaryButton disabled={busy} onClick={() => setPending({ kind: 'others' })}>
                {t('revokeOthers')}
              </SecondaryButton>
            </div>
          ) : null}

          <p className="hint">{t('capNote')}</p>
        </>
      )}

      {pending === undefined ? null : (
        <ConfirmPopover
          title={
            pending.kind === 'others'
              ? t('revokeOthersConfirmTitle')
              : pending.kind === 'current'
                ? t('revokeCurrentConfirmTitle')
                : t('revokeOneConfirmTitle')
          }
          body={
            pending.kind === 'others'
              ? t('revokeOthersConfirmBody')
              : pending.kind === 'current'
                ? t('revokeCurrentConfirmBody')
                : t('revokeOneConfirmBody', {
                    device: pending.session.deviceLabel ?? t('unknownDevice'),
                  })
          }
          {...(error === undefined ? {} : { error })}
          cancelLabel={tCommon('cancel')}
          confirmLabel={busy ? t('revoking') : confirmLabelFor(pending.kind)}
          confirmDisabled={busy}
          onCancel={() => {
            if (busy) return
            setError(undefined)
            setPending(undefined)
          }}
          onConfirm={() => void onConfirm()}
        />
      )}
    </>
  )
}

function DeviceRow({
  row,
  label,
  lastSeen,
  firstSeen,
  currentLabel,
  revokeLabel,
  disabled,
  onRevoke,
}: {
  readonly row: DeviceSession
  readonly locale: Locale
  readonly label: string
  readonly lastSeen: string
  readonly firstSeen: string
  readonly currentLabel: string
  readonly revokeLabel: string
  readonly disabled: boolean
  onRevoke(): void
}): JSX.Element {
  return (
    <div className="row">
      <div className="l">
        <Icon name="smartphone" size={14} />{' '}
        {/* A device label is a user-agent parse — "iPhone · Safari" — so it is Latin text in an
            RTL shell and needs the same isolation every code and phone number gets. */}
        <Bidi>{label}</Bidi>{' '}
        {/* Beside the label, not in the action column: the marker describes *this row*, and the
            right column has a button in it on every row. Stacked there, the current device's row
            grew a second line that no other row had — the one row every viewer always sees. */}
        {row.isCurrent ? <Chip>{currentLabel}</Chip> : null}
        <div className="gsub">
          {lastSeen} · {firstSeen}
        </div>
      </div>
      <div className="r">
        <button className="steel-chip" type="button" disabled={disabled} onClick={onRevoke}>
          {revokeLabel}
        </button>
      </div>
    </div>
  )
}
